import { FilterOperatorType } from '../../shared/components/odata-filter/models/filter-operator-type';
import { ODataFilter } from '../../shared/components/odata-filter/models/odata-filter';
import { FilterOperator, OPERATORS } from '../../shared/components/odata-filter/models/operators';
import { SearchValueType } from '../../shared/components/odata-filter/models/search-value-type';
import { SelectedFilter } from '../../shared/components/odata-filter/models/selected-filter';
import { AiFilterGroup, AiPeopleFilterRule, isFilterGroup } from '../models/ai-people-intent.model';

export class AiFilterMapper {
  static isFlatAnd(group: AiFilterGroup): boolean {
    return group?.logic === 'and' && (group.rules ?? []).every((r) => !isFilterGroup(r));
  }

  static toSelectedFilters(group: AiFilterGroup, collection: ODataFilter[]): SelectedFilter[] {
    return (group?.rules ?? []).map((r) =>
      AiFilterMapper.mapRule(r as AiPeopleFilterRule, collection)
    );
  }

  static toFilterTree(group: AiFilterGroup, collection: ODataFilter[]): any {
    const parts = (group?.rules ?? []).map((r) =>
      isFilterGroup(r)
        ? AiFilterMapper.toFilterTree(r, collection)
        : AiFilterMapper.toFilterObject(AiFilterMapper.mapRule(r as AiPeopleFilterRule, collection))
    );
    return parts.length === 1 ? parts[0] : { [group.logic]: parts };
  }

  private static mapRule(rule: AiPeopleFilterRule, candidates: ODataFilter[]): SelectedFilter {
    const model = AiFilterMapper.resolveField(rule, candidates);
    const selected = new SelectedFilter({
      filter: model,
      filterOperator: AiFilterMapper.resolveOperator(model.valueType, rule.operator),
      searchValue: AiFilterMapper.coerceValue(model, rule.value),
      searchValues: AiFilterMapper.toSearchValues(model, rule.values)
    });
    selected.selectedSubFilters = (rule.subFilters ?? []).map((sub) =>
      AiFilterMapper.mapRule(sub, model.subFilter?.filters ?? [])
    );
    return selected;
  }

  private static toFilterObject(selected: SelectedFilter): any {
    return selected.filter.buildFilter(
      selected.searchValues.length > 0 ? selected.searchValues : selected.searchValue,
      selected.filterOperator,
      selected.getSubQueryFilters(false)
    );
  }

  private static resolveField(rule: AiPeopleFilterRule, candidates: ODataFilter[]): ODataFilter {
    const model = rule.fieldId
      ? candidates.find((f) => f.targetFieldId === rule.fieldId)
      : candidates.find((f) => f.displayName === rule.displayName);
    if (!model) {
      throw new Error(`AI filter references unknown field '${rule.displayName ?? rule.fieldId}'.`);
    }
    return model;
  }

  private static resolveOperator(
    valueType: SearchValueType,
    op: FilterOperatorType
  ): FilterOperator {
    const found = OPERATORS[valueType]?.operators.find((o) => o.displayOperator === op);
    if (!found) {
      throw new Error(`Operator '${op}' is not valid for value type '${valueType}'.`);
    }
    return found;
  }

  private static toSearchValues(model: ODataFilter, values?: string[]) {
    if (!values?.length) return [];
    const list = model.values ?? [];
    return values.map((v) => {
      const match = list.find(
        (x) => x.searchValue == v || (x.name ?? '').toLowerCase() === v.toLowerCase()
      );
      return match ?? { searchValue: v, name: v };
    });
  }

  private static coerceValue(model: ODataFilter, value?: string): any {
    if (value == null) return null;
    if (model.valueType === SearchValueType.Number) return Number(value);
    if (model.valueType === SearchValueType.Boolean) return value === 'true';
    return value;
  }
}
